/**
 * Directiva `v-outside` para detectar eventos fuera de un elemento.
 *
 * @usage 
 * ```html
 * <div v-outside:event="{ exclude: ['ref1', 'ref2'], handler: onClose }"></div>
 * ```
 *
 * @argument {string|string[]} [arg] Eventos que activan la comprobación (por defecto: `click` y `touchstart`). Si se pasa como string, puede ser separado por `:` (ej. `click:touchstart`).
 * @param {Object} value
 * @param {Function} value.handler Función a ejecutar si el clic ocurre fuera del elemento y de los excluidos.
 * @param {string[]} [value.exclude] Lista de referencias (`$refs`) a excluir del control de clic fuera.
 *
 * @example
 * v-outside:click:touchstart="{ exclude: ['dropdown'], handler: closeDropdown }"
 */
export const Outside = {
  bind: function (el, binding, vnode) {
    const touch = 'touchstart'
    const click = 'click'
    var events = binding.arg
    if (!events) {
      events = [click, touch]
    } else {
      if (!Array.isArray(events)) {
        events = String(events).split(':')
      }
      events = events.filter(Boolean)
      if (events.includes(click) && !events.includes(touch)) {
        events.push(touch)
      } else if (!events.includes(click) && events.includes(touch)) {
        events.push(touch)
      }
    }
    const handleOutside = (e) => {
      e.stopPropagation()
      const {
        handler,
        exclude
      } = binding.value
      let onExcludedEl = false
      // Gives you the ability to exclude certain elements if you want, pass as array of strings to exclude
      if (exclude) {
        exclude.forEach(refName => {
          if (!onExcludedEl) {
            const excludedEl = refName instanceof HTMLElement || refName?.$el ? refName : vnode.context.$refs[refName]
            let excludedDomEl = null
            if (excludedEl) {
              // If it's a vue component grab the element, otherwise it is the element
              excludedDomEl = excludedEl.$el ? excludedEl.$el : excludedEl
              onExcludedEl = excludedDomEl.contains(e.target)
            }
          }
        })
      }
      if (!el.contains(e.target) && !onExcludedEl) {
        handler(e)
      }
    }
    el._outside = { events, handleOutside }
    for (const event of events) {
      document.addEventListener(event, handleOutside)
    }
  },
  unbind: function (el, binding, vnode) {
    const { events, handleOutside } = el._outside || { events: [] }
    for (const event of events) {
      document.removeEventListener(event, handleOutside)
    }
    delete el._outside
  }
}
